import { defineMessage, useIntl } from "react-intl";
import { Environment, graphql, usePaginationFragment } from "react-relay";
import LoadMore from "@/components/LoadMore";
import TopicListItem from "@/components/TopicListItem";
import ErrorPage from "@/pages/_error";
import { UserTopicsPageFragment$key } from "./__generated__/UserTopicsPageFragment.graphql";
import { LoaderArgs } from "@/utils/route";
import { UserTopicsPageQuery as UserTopicsPageQueryType } from "./__generated__/UserTopicsPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
const UserTopicsPageFragment = graphql`
  fragment UserTopicsPageFragment on User
  @refetchable(queryName: "UserTopicsPageFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    topics(first: $count, after: $cursor, order: NEWEST)
      @connection(key: "UserTopicsPageFragment_topics") {
      edges {
        node {
          id
          ...TopicListItemFragment
        }
      }
    }
  }
`;
export default function UserTopicsPage() {
  const intl = useIntl();
  const {
    variables,
    query: { entityByUsername: user },
  } = useUserTopicsPageQuery();
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    UserTopicsPageFragment,
    user as UserTopicsPageFragment$key,
  );
  if (!data) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {data.topics.edges.map((topic) => (
        <TopicListItem key={topic.node.id} topic={topic.node} />
      ))}
      <LoadMore
        hasMore={hasNext}
        loadMore={loadNext}
        isLoading={isLoadingNext}
      />
      {data.topics.edges.length === 0 && (
        <p className="text-gray-400">No topics</p>
      )}
    </div>
  );
}
const UserTopicsPageQuery = graphql`
  query UserTopicsPageQuery($username: String!) {
    entityByUsername(username: $username) {
      ...UserTopicsPageFragment
    }
  }
`;
export function loader(environment: Environment) {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<UserTopicsPageQueryType>(environment, UserTopicsPageQuery, {
      username,
    });
  };
}
const useUserTopicsPageQuery = () => usePreloaded<UserTopicsPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Discussions" }),
};
